import { Component, EventBus } from '../core/smpl-components.js'
import { Events } from '../core/events.js'
import EmblaCarousel from 'embla-carousel'
import { gsap } from 'gsap'
import debounce from 'lodash/debounce'

/** Statistics highlight
 *
 * A row of stat cards (4 in view) backed by an Embla slider. A single arrow
 * drives two behaviours:
 *   - foldout closed → page the view forward by however many cards are in view
 *     (next 4, or the remaining 2 at the end);
 *   - foldout open   → step the active item by one (clamped to the ends,
 *     arrows disable there), scrolling its page into view when needed.
 *
 * Clicking a card opens the shared foldout below the row with that card's body;
 * clicking it again closes it. All bodies live in the DOM (crawlable); only the
 * active one paints. The foldout is a clip container (height:0 / overflow:hidden)
 * whose height we animate to the active body's natural height — the same reveal
 * trick as the main-nav foldout, so differing body heights just work.
 */
class StatisticsHighlight {
	constructor(options) {
		this.options = { ...options }
		this.element = this.options.el

		this.elSlider = this.element.querySelector('.js-stats-slider')
		this.elFoldout = this.element.querySelector('.js-stats-foldout')
		this.elFoldoutInner = this.elFoldout.querySelector('.statistics-foldout__inner')
		this.elNext = this.element.querySelector('.js-stats-next')
		this.elPrev = this.element.querySelector('.js-stats-prev')

		this.slides = Array.from(this.element.querySelectorAll('.js-stats-slide'))
		this.bodies = Array.from(this.element.querySelectorAll('.js-stats-body'))

		this.activeId = null // null = foldout closed
		this.tl = null

		this.init()
	}

	init() {
		this.embla = EmblaCarousel(this.elSlider, {
			align: 'start',
			skipSnaps: true,
			containScroll: 'trimSnaps',
			loop: false,
			slidesToScroll: 1,
			// Only count a slide as "in view" when it's (near) fully visible, so a
			// partially-clipped active card triggers a scroll instead of staying put.
			inViewThreshold: 0.99,
		})

		// -- listeners -------------------------------------------------------- //
		this.bndOnNextClick = (e) => this.onArrowClick(e, 1)
		this.bndOnPrevClick = (e) => this.onArrowClick(e, -1)
		this.elNext.addEventListener('click', this.bndOnNextClick)
		if (this.elPrev) this.elPrev.addEventListener('click', this.bndOnPrevClick)

		this.bndOnSlideClick = this.onSlideClick.bind(this)
		this.slides.forEach((slide) => slide.addEventListener('click', this.bndOnSlideClick))

		this.bndUpdateArrow = this.updateArrow.bind(this)
		this.debouncedUpdateArrow = debounce(this.bndUpdateArrow, 100)
		this.embla.on('select', this.bndUpdateArrow)
		this.embla.on('resize', this.debouncedUpdateArrow)

		this.updateArrow()
	}

	// -- helpers ------------------------------------------------------------- //
	getInView() {
		// Indices of the fully-visible slides, ascending.
		return this.embla
			.slidesInView()
			.slice()
			.sort((a, b) => a - b)
	}

	getPerView() {
		return Math.max(1, this.getInView().length)
	}

	// -- arrow --------------------------------------------------------------- //
	// dir: 1 = next, -1 = prev.
	onArrowClick(e, dir) {
		e.preventDefault()

		if (this.activeId === null) {
			// Closed: page the view by the number of visible cards.
			const inView = this.getInView()
			const start = inView.length ? inView[0] : 0
			this.embla.scrollTo(start + dir * this.getPerView())
			return
		}

		// Open: step the active item by one, clamped to the ends.
		const next = this.activeId + dir
		if (next < 0 || next >= this.slides.length) return

		this.open(next)

		// Bring the new item into view if it isn't (near) fully visible.
		if (!this.getInView().includes(next)) {
			this.embla.scrollTo(next)
		}
	}

	updateArrow() {
		// Closed: bounded by scroll position. Open: bounded by the active item.
		const atStart = this.activeId === null ? !this.embla.canScrollPrev() : this.activeId <= 0
		const atEnd = this.activeId === null ? !this.embla.canScrollNext() : this.activeId >= this.slides.length - 1
		this.elNext.classList.toggle('is-disabled', atEnd)
		if (this.elPrev) this.elPrev.classList.toggle('is-disabled', atStart)
	}

	// -- foldout ------------------------------------------------------------- //
	onSlideClick(e) {
		const id = Number(e.currentTarget.dataset.id)
		if (id === this.activeId) {
			this.close()
		} else {
			this.open(id)
		}
	}

	setActiveBody(id) {
		this.bodies.forEach((body) => {
			body.classList.toggle('is-active', Number(body.dataset.id) === id)
		})
		this.slides.forEach((slide) => {
			const on = Number(slide.dataset.id) === id
			slide.classList.toggle('is-active', on)
			slide.setAttribute('aria-expanded', on ? 'true' : 'false')
		})
	}

	open(id) {
		const wasClosed = this.activeId === null
		this.activeId = id

		if (this.tl) this.tl.kill()
		this.elFoldout.setAttribute('aria-hidden', 'false')
		this.elFoldout.classList.add('is-open')

		if (wasClosed) {
			// Closed → open: reveal from height 0.
			this.setActiveBody(id)
			const target = this.elFoldout.scrollHeight

			this.tl = gsap.timeline()
			this.tl.fromTo(
				this.elFoldout,
				{ height: 0, overflow: 'hidden' },
				{
					height: target,
					duration: 0.5,
					ease: 'power3.inOut',
					onComplete: () => gsap.set(this.elFoldout, { clearProps: 'height,overflow' }),
				},
			)
			this.tl.fromTo(
				this.elFoldoutInner.querySelector('.is-active'),
				{ autoAlpha: 0, y: 20 },
				{ autoAlpha: 1, y: 0, duration: 0.5, ease: 'power2.out' },
				'-=0.3',
			)
		} else {
			// Already open: swap body and animate height to the new content.
			const current = this.elFoldout.getBoundingClientRect().height
			this.setActiveBody(id)
			const target = this.elFoldout.scrollHeight

			this.tl = gsap.timeline()
			this.tl.fromTo(
				this.elFoldout,
				{ height: current, overflow: 'hidden' },
				{
					height: target,
					duration: 0.4,
					ease: 'power3.inOut',
					onComplete: () => gsap.set(this.elFoldout, { clearProps: 'height,overflow' }),
				},
			)
			this.tl.fromTo(this.elFoldoutInner.querySelector('.is-active'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, '-=0.2')
		}

		this.updateArrow()
		EventBus.emit(Events.PAGE_RESIZE)
	}

	close() {
		if (this.activeId === null) return

		if (this.tl) this.tl.kill()

		const current = this.elFoldout.getBoundingClientRect().height

		this.tl = gsap.timeline({
			onComplete: () => {
				this.setActiveBody(-1)
				this.elFoldout.classList.remove('is-open')
				this.elFoldout.setAttribute('aria-hidden', 'true')
				gsap.set(this.elFoldout, { clearProps: 'height,overflow' })
				this.updateArrow()
				EventBus.emit(Events.PAGE_RESIZE)
			},
		})
		this.tl.fromTo(this.elFoldout, { height: current, overflow: 'hidden' }, { height: 0, duration: 0.4, ease: 'power3.inOut' })

		this.activeId = null
	}

	destroy() {
		if (this.tl) this.tl.kill()
		this.embla.off('select', this.bndUpdateArrow)
		this.embla.off('resize', this.debouncedUpdateArrow)
		this.embla.destroy()
		this.elNext.removeEventListener('click', this.bndOnNextClick)
		if (this.elPrev) this.elPrev.removeEventListener('click', this.bndOnPrevClick)
		this.slides.forEach((slide) => slide.removeEventListener('click', this.bndOnSlideClick))
	}
}

// -----------------------------------------------------------------------------
export default class extends Component {
	prepare() {
		this.statisticsHighlight = new StatisticsHighlight({
			el: this.element,
		})
	}

	mount() {}

	unmount() {
		if (this.statisticsHighlight) {
			this.statisticsHighlight.destroy()
			this.statisticsHighlight = null
		}
	}
}
